import { useSyncExternalStore } from 'react';

/**
 * The human bearer token (Supabase access token, or a pasted dev token —
 * see LoginPage). Kept outside React so the plain `api.ts` module can read
 * it without a hook, with a small external-store shim for reactive UI.
 */

const TOKEN_KEY = 'gs_session_token';
const ORG_KEY = 'gs_selected_org_id';

type Listener = () => void;
const listeners = new Set<Listener>();
function notify() {
  for (const listener of listeners) listener();
}

let token: string | null = safeGet(TOKEN_KEY);
let organisationId: string | null = safeGet(ORG_KEY);

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function getToken(): string | null {
  return token;
}

export function setToken(next: string | null): void {
  token = next;
  try {
    if (next) localStorage.setItem(TOKEN_KEY, next);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* storage unavailable — token still works for this tab session */
  }
  notify();
}

export function getSelectedOrganisationId(): string | null {
  return organisationId;
}

export function setSelectedOrganisationId(next: string | null): void {
  organisationId = next;
  try {
    if (next) localStorage.setItem(ORG_KEY, next);
    else localStorage.removeItem(ORG_KEY);
  } catch {
    /* ignore */
  }
  notify();
}

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useToken(): string | null {
  return useSyncExternalStore(subscribe, getToken);
}

export function useSelectedOrganisationId(): string | null {
  return useSyncExternalStore(subscribe, getSelectedOrganisationId);
}

/** For use inside <RequireOrganisation> only — org id is guaranteed present there. */
export function useOrganisationId(): string {
  const id = useSelectedOrganisationId();
  if (!id) throw new Error('useOrganisationId called outside <RequireOrganisation>');
  return id;
}
